import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PhoneCall, MessageSquare, Users, CalendarCheck, Download, AlertTriangle,
  ChevronRight, X, Phone, ShieldCheck, Shield, ShieldAlert, CheckCircle2, Clock,
  Filter, Search, ChevronDown, Award, RefreshCw, ArrowUpRight, UserCheck
} from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import { initials } from '../lib/format.js';
import toast from 'react-hot-toast';

const RANGE_PRESETS = [
  { key: 'daily', label: 'Today (Daily)' },
  { key: 'weekly', label: 'This Week' },
  { key: 'monthly', label: 'This Month' },
  { key: 'custom', label: 'Custom (Up to 90 Days)' },
];

export default function Reports() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [directOnly, setDirectOnly] = useState(false);
  const [timeframe, setTimeframe] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('all');
  const [inspectingAgentId, setInspectingAgentId] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const customActive = timeframe === 'custom';

  const buildQuery = () => {
    const params = { timeframe };
    if (customActive && startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (selectedAgentId && selectedAgentId !== 'all') {
      params.agentId = selectedAgentId;
    }
    if (directOnly) {
      params.directOnly = 'true';
    }
    return params;
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['team-analytics', timeframe, startDate, endDate, selectedAgentId, directOnly],
    queryFn: () => apiGet('/analytics/team', buildQuery()),
    staleTime: 15_000,
  });

  const isStandardAgent = data?.isStandardAgent === true;
  const teammates = data?.teammates || [];
  const items = data?.items || [];
  const summary = data?.summary || {};
  const totalsRow = data?.totalsRow || {};
  const viewLabel = data?.label || 'Current Period';

  // Pagination
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  // CSV Export
  const handleDownloadCsv = () => {
    if (!items.length) {
      toast.error('No data available to export');
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Agent ID',
      'Total Leads',
      'Leads Sold',
      'Inbound Texts',
      'Outbound Texts',
      'Inbound Calls',
      'Outbound Calls',
      'Carrier Violations',
      'Talk Time (Inbound)',
      'Talk Time (Outbound)',
      'Talk Time (Total)',
      'Logged-in Time',
      'Coaching Alert'
    ];

    const escapeCsv = (str) => `"${String(str || '').replace(/"/g, '""')}"`;

    const csvRows = [headers.join(',')];

    items.forEach((row) => {
      csvRows.push([
        escapeCsv(row.name),
        escapeCsv(row.email),
        escapeCsv(row.agentId),
        row.totalLeads,
        row.leadsSold,
        row.inboundTexts,
        row.outboundTexts,
        row.inboundCalls,
        row.outboundCalls,
        row.carrierViolations,
        escapeCsv(row.talkTimeInbound),
        escapeCsv(row.talkTimeOutbound),
        escapeCsv(row.talkTimeTotal),
        escapeCsv(row.loggedInTime),
        escapeCsv(row.coachingAlert ? `FLAG: ${row.alertReason}` : 'Normal')
      ].join(','));
    });

    if (totalsRow) {
      csvRows.push([
        escapeCsv('Totals'),
        '',
        '--',
        totalsRow.totalLeads,
        totalsRow.leadsSold,
        totalsRow.inboundTexts,
        totalsRow.outboundTexts,
        totalsRow.inboundCalls,
        totalsRow.outboundCalls,
        totalsRow.carrierViolations,
        escapeCsv(totalsRow.talkTimeInbound),
        escapeCsv(totalsRow.talkTimeOutbound),
        escapeCsv(totalsRow.talkTimeTotal),
        escapeCsv(totalsRow.loggedInTime),
        ''
      ].join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `CIVIX_Team_Analytics_${viewLabel.replace(/[^a-zA-Z0-9]/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Team Analytics CSV exported');
  };

  // If standard agent without downline teammates
  if (isStandardAgent) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="panel p-8 text-center space-y-4 border border-civix-border/60">
          <div className="w-16 h-16 mx-auto rounded-full bg-civix-purple/15 border border-civix-purple/30 flex items-center justify-center">
            <UserCheck className="w-8 h-8 text-civix-purple" />
          </div>
          <h2 className="text-2xl font-bold">Personal Agent Workspace</h2>
          <p className="text-white/60 max-w-lg mx-auto text-sm leading-relaxed">
            The Team Analytics Hub is tailored for managers coaching sales teams. You track your personal dials, sales, call recordings, and lead pipeline directly on your personal Dashboard.
          </p>
          <div className="pt-4">
            <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2">
              Go to My Dashboard <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1700px] mx-auto pb-12">
      {/* Top Bar with Date Label and CSV Download */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-civix-cyan font-semibold">
            Performance & Coaching
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2 mt-0.5">
            Team Analytics Hub
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-xs font-mono bg-purple-950/40 border border-purple-500/30 px-3 py-1.5 rounded-lg text-purple-200">
            Now viewing: <strong className="text-white">{viewLabel}</strong>
          </div>

          <button
            onClick={handleDownloadCsv}
            disabled={!items.length}
            className="btn-primary text-xs flex items-center gap-1.5 py-1.5 px-3 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-lg shadow-lg shadow-purple-900/30 transition-all cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />
            DOWNLOAD CSV
          </button>

          <button
            onClick={() => refetch()}
            className="p-1.5 rounded-lg border border-civix-border hover:bg-white/5 text-white/60 hover:text-white transition-colors"
            title="Refresh analytics"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter and Range Controls Bar */}
      <div className="panel p-4 space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Super Admin Global Switcher */}
            {isSuperAdmin && (
              <div className="inline-flex items-center bg-black/40 border border-civix-border/70 rounded-lg p-1 gap-1 shadow-inner">
                <button
                  type="button"
                  onClick={() => { setDirectOnly(false); setSelectedAgentId('all'); setCurrentPage(1); }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition flex items-center gap-1.5 cursor-pointer ${
                    !directOnly
                      ? 'bg-civix-purple text-white shadow-sm font-semibold'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  View All Agency Data
                </button>
                <button
                  type="button"
                  onClick={() => { setDirectOnly(true); setSelectedAgentId('all'); setCurrentPage(1); }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition flex items-center gap-1.5 cursor-pointer ${
                    directOnly
                      ? 'bg-civix-purple text-white shadow-sm font-semibold'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Shield className="w-3.5 h-3.5" />
                  View My Direct Team
                </button>
              </div>
            )}

            {/* Team / Agent Filter Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-white/50 font-medium">Select agents:</label>
              <select
                value={selectedAgentId}
                onChange={(e) => {
                  setSelectedAgentId(e.target.value);
                  setCurrentPage(1);
                }}
                className="bg-black/50 border border-civix-border rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-civix-cyan min-w-[200px]"
              >
                <option value="all">
                  {directOnly ? 'All Direct Teammates' : isSuperAdmin ? 'All Agency Teammates' : 'All Teammates'} ({teammates.length})
                </option>
                {teammates.map((t) => (
                  <option key={String(t._id)} value={String(t._id)}>
                    {t.isSelf ? 'My Own Records' : `${t.name} (${t.email})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Timeframe Presets */}
            <div className="inline-flex items-center bg-black/40 border border-civix-border rounded-lg p-1 gap-1">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setTimeframe(p.key);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition ${
                    timeframe === p.key
                      ? 'bg-civix-purple text-white shadow-sm'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {customActive && (
            <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-lg border border-civix-border text-xs">
              <span className="text-white/40">From:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent text-white border-0 focus:outline-none text-xs"
              />
              <span className="text-white/40">To:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent text-white border-0 focus:outline-none text-xs"
              />
            </div>
          )}
        </div>
      </div>

      {/* Top Row Quick Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="panel p-4 flex items-center justify-between border-l-4 border-l-civix-cyan">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/50 font-medium">Total Calls Placed</div>
            <div className="text-2xl font-bold font-mono mt-0.5 text-white">
              {(summary.totalCalls || 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">Inbound & Outbound combined</div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-civix-cyan/10 border border-civix-cyan/30 flex items-center justify-center text-civix-cyan">
            <PhoneCall className="w-5 h-5" />
          </div>
        </div>

        <div className="panel p-4 flex items-center justify-between border-l-4 border-l-purple-500">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/50 font-medium">Total Talk Time</div>
            <div className="text-2xl font-bold font-mono mt-0.5 text-purple-300">
              {summary.totalTalkTime || '00h 00m'}
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">Active voice conversations</div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        <div className="panel p-4 flex items-center justify-between border-l-4 border-l-emerald-500">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/50 font-medium">Total SMS Sent</div>
            <div className="text-2xl font-bold font-mono mt-0.5 text-emerald-400">
              {(summary.totalSmsSent || 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">Outbound carrier messages</div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
            <MessageSquare className="w-5 h-5" />
          </div>
        </div>

        <div className="panel p-4 flex items-center justify-between border-l-4 border-l-amber-500">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/50 font-medium">Sales / Appointments</div>
            <div className="text-2xl font-bold font-mono mt-0.5 text-amber-300">
              {summary.leadsSold || 0} / {summary.appointmentsBooked || 0}
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">Closed deals / booked slots</div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
            <Award className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Master Team Performance Table */}
      <div className="panel overflow-hidden border border-civix-border/70">
        <div className="p-4 border-b border-civix-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-civix-cyan" />
            <h2 className="text-sm font-semibold text-white">Call Statistics & Agent Performance</h2>
            <span className="text-xs text-white/40">({items.length} agents)</span>
          </div>

          <div className="flex items-center gap-3 text-xs text-white/50">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              Coaching Alert: Logged in &gt;6h with &lt;30m talk time
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-xs text-white/50 space-y-2">
            <div className="w-8 h-8 mx-auto border-2 border-civix-cyan border-t-transparent rounded-full animate-spin"></div>
            <div>Aggregating team metrics across calls, SMS, and sessions...</div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-rose-300 text-xs">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-rose-400" />
            Failed to load team analytics: {error?.message}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-white/50 text-xs">
            No active teammates found for the selected filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-black/40 text-white/60 uppercase tracking-wider border-b border-civix-border font-medium">
                  <th className="py-3 px-3">Name</th>
                  <th className="py-3 px-2">Agent ID</th>
                  <th className="py-3 px-2 text-right">Total leads</th>
                  <th className="py-3 px-2 text-right">Leads sold</th>
                  <th className="py-3 px-2 text-right">Inbound texts</th>
                  <th className="py-3 px-2 text-right">Outbound texts</th>
                  <th className="py-3 px-2 text-right">Inbound calls</th>
                  <th className="py-3 px-2 text-right">Outbound calls</th>
                  <th className="py-3 px-2 text-right">Carrier violations</th>
                  <th className="py-3 px-2 text-right">Talk time (inbound)</th>
                  <th className="py-3 px-2 text-right">Talk time (outbound)</th>
                  <th className="py-3 px-2 text-right">Talk time (total)</th>
                  <th className="py-3 px-3 text-right">Logged in time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-civix-border/40">
                {paginatedItems.map((row) => (
                  <tr
                    key={row.agentId}
                    onClick={() => setInspectingAgentId(row.agentId)}
                    className="hover:bg-white/[0.03] transition-colors cursor-pointer group"
                  >
                    {/* Name + Email */}
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-civix-cyan transition-transform group-hover:translate-x-0.5" />
                        <div>
                          <div className="font-semibold text-white group-hover:text-civix-cyan transition-colors">
                            {row.name}
                          </div>
                          <div className="text-[10px] text-white/40 truncate max-w-[160px]">
                            {row.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Agent ID */}
                    <td className="py-2.5 px-2 font-mono text-[10px] text-white/40">
                      {row.agentId.slice(-6)}
                    </td>

                    {/* Total Leads */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/80">
                      {row.totalLeads}
                    </td>

                    {/* Leads Sold */}
                    <td className="py-2.5 px-2 text-right font-mono font-medium text-emerald-400">
                      {row.leadsSold}
                    </td>

                    {/* Inbound Texts */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/70">
                      {row.inboundTexts}
                    </td>

                    {/* Outbound Texts */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/80">
                      {row.outboundTexts}
                    </td>

                    {/* Inbound Calls */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/70">
                      {row.inboundCalls}
                    </td>

                    {/* Outbound Calls */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/80">
                      {row.outboundCalls}
                    </td>

                    {/* Carrier Violations */}
                    <td className="py-2.5 px-2 text-right font-mono">
                      {row.carrierViolations > 0 ? (
                        <span className="px-1.5 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-bold">
                          {row.carrierViolations} ⚠️
                        </span>
                      ) : (
                        <span className="text-white/30">0</span>
                      )}
                    </td>

                    {/* Talk Time Inbound */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/60">
                      {row.talkTimeInbound}
                    </td>

                    {/* Talk Time Outbound */}
                    <td className="py-2.5 px-2 text-right font-mono text-white/60">
                      {row.talkTimeOutbound}
                    </td>

                    {/* Talk Time Total */}
                    <td className="py-2.5 px-2 text-right font-mono font-medium text-civix-cyan">
                      {row.talkTimeTotal}
                    </td>

                    {/* Logged in time + Coaching Alert Flag */}
                    <td className="py-2.5 px-3 text-right font-mono">
                      {row.coachingAlert ? (
                        <div className="inline-flex items-center gap-1 text-amber-300 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30" title={row.alertReason}>
                          <span>{row.loggedInTime}</span>
                          <span className="text-[10px] px-1 bg-amber-400 text-black rounded font-black">FLAG ⚠️</span>
                        </div>
                      ) : (
                        <span className="text-white/80">{row.loggedInTime}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* Totals Summary Row */}
              {totalsRow && (
                <tfoot>
                  <tr className="bg-black/60 font-bold border-t-2 border-civix-border text-white">
                    <td className="py-3 px-3">Totals</td>
                    <td className="py-3 px-2 font-mono text-white/40">--</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.totalLeads}</td>
                    <td className="py-3 px-2 text-right font-mono text-emerald-400">{totalsRow.leadsSold}</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.inboundTexts}</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.outboundTexts}</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.inboundCalls}</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.outboundCalls}</td>
                    <td className="py-3 px-2 text-right font-mono">
                      {totalsRow.carrierViolations > 0 ? (
                        <span className="text-rose-400">{totalsRow.carrierViolations}</span>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.talkTimeInbound}</td>
                    <td className="py-3 px-2 text-right font-mono">{totalsRow.talkTimeOutbound}</td>
                    <td className="py-3 px-2 text-right font-mono text-civix-cyan">{totalsRow.talkTimeTotal}</td>
                    <td className="py-3 px-3 text-right font-mono text-white">{totalsRow.loggedInTime}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        <div className="p-3 bg-black/30 border-t border-civix-border flex items-center justify-between text-xs text-white/50">
          <div className="flex items-center gap-2">
            <span>Users per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-black/50 border border-civix-border rounded px-2 py-0.5 text-white focus:outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <span>
              {items.length === 0
                ? '0 of 0'
                : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, items.length)} of ${items.length}`}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-2 py-1 rounded border border-civix-border hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                &lt;
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-2 py-1 rounded border border-civix-border hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Agent Profile Drawer */}
      {inspectingAgentId && (
        <AgentInspectionDrawer
          agentId={inspectingAgentId}
          onClose={() => setInspectingAgentId(null)}
        />
      )}
    </div>
  );
}

/**
 * Agent Profile Inspection Drawer component
 */
function AgentInspectionDrawer({ agentId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-inspection', agentId],
    queryFn: () => apiGet(`/analytics/agent/${agentId}`),
    staleTime: 10_000,
  });

  const agent = data?.agent || {};
  const recentCalls = data?.recentCalls || [];
  const recentLeads = data?.recentLeads || [];
  const phoneNumbers = data?.phoneNumbers || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="w-full max-w-xl h-full bg-[#0D1520] border-l border-civix-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-civix-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-civix-purple/20 border border-civix-purple/40 flex items-center justify-center font-bold text-civix-purpleSoft">
              {initials(agent.name || 'A')}
            </div>
            <div>
              <h3 className="font-bold text-white text-base">{agent.name || 'Loading...'}</h3>
              <div className="text-xs text-white/50">{agent.email} · {agent.role}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {isLoading ? (
            <div className="py-16 text-center text-xs text-white/50">
              Loading agent details...
            </div>
          ) : error ? (
            <div className="py-8 text-center text-rose-400 text-xs">
              Failed to load agent profile: {error.message}
            </div>
          ) : (
            <>
              {/* Profile Snapshot */}
              <div className="grid grid-cols-3 gap-2">
                <div className="panel-light p-3 rounded">
                  <div className="text-[10px] uppercase text-white/40 font-semibold">Wallet Balance</div>
                  <div className="text-lg font-bold font-mono text-emerald-400 mt-1">
                    ${Number(agent.walletBalance || 0).toFixed(2)}
                  </div>
                </div>
                <div className="panel-light p-3 rounded">
                  <div className="text-[10px] uppercase text-white/40 font-semibold">A2P Status</div>
                  <div className="mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 uppercase">
                      {agent.a2pStatus || 'verified'}
                    </span>
                  </div>
                </div>
                <div className="panel-light p-3 rounded">
                  <div className="text-[10px] uppercase text-white/40 font-semibold">Assigned Line</div>
                  <div className="text-xs font-mono text-white/80 mt-1 truncate">
                    {phoneNumbers[0]?.number || 'No line'}
                  </div>
                </div>
              </div>

              {/* Assigned Phone Numbers & Health */}
              <div className="panel-light p-3.5 rounded space-y-2">
                <div className="text-xs font-bold text-white flex items-center justify-between">
                  <span>Assigned Numbers & Carrier Health</span>
                  <span className="text-[10px] text-white/40">{phoneNumbers.length} active</span>
                </div>
                {phoneNumbers.length === 0 ? (
                  <div className="text-[11px] text-white/40">No numbers assigned to this agent yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {phoneNumbers.map((p) => (
                      <div key={p.number} className="flex items-center justify-between text-xs p-2 rounded bg-black/30 border border-white/5">
                        <span className="font-mono text-white font-medium">{p.number}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-civix-cyan">Area {p.areaCode}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                            Score: {p.healthScore || 100}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Call History */}
              <div className="panel-light p-3.5 rounded space-y-2">
                <div className="text-xs font-bold text-white flex items-center justify-between">
                  <span>Recent Calls</span>
                  <span className="text-[10px] text-white/40">Last 10 attempts</span>
                </div>
                {recentCalls.length === 0 ? (
                  <div className="text-[11px] text-white/40">No call activity recorded yet.</div>
                ) : (
                  <div className="space-y-1">
                    {recentCalls.map((c) => (
                      <div key={c._id} className="flex items-center justify-between text-[11px] p-2 rounded bg-black/20 border border-white/5">
                        <div>
                          <div className="font-medium text-white">
                            {c.direction === 'inbound' ? '↙ Inbound' : '↗ Outbound'} · {c.lead?.firstName ? `${c.lead.firstName} ${c.lead.lastName || ''}` : c.to || c.from || 'Call'}
                          </div>
                          <div className="text-[10px] text-white/40">
                            {new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-civix-cyan">{c.duration || 0}s</span>
                          <div className="text-[9px] uppercase text-white/40">{c.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Assigned Leads */}
              <div className="panel-light p-3.5 rounded space-y-2">
                <div className="text-xs font-bold text-white flex items-center justify-between">
                  <span>Recent Leads Assigned</span>
                  <span className="text-[10px] text-white/40">Latest 10 leads</span>
                </div>
                {recentLeads.length === 0 ? (
                  <div className="text-[11px] text-white/40">No leads assigned to this agent yet.</div>
                ) : (
                  <div className="space-y-1">
                    {recentLeads.map((l) => (
                      <div key={l._id} className="flex items-center justify-between text-[11px] p-2 rounded bg-black/20 border border-white/5">
                        <div>
                          <div className="font-medium text-white">{l.firstName} {l.lastName}</div>
                          <div className="text-[10px] text-white/40">{l.phone}</div>
                        </div>
                        <div className="text-right">
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-civix-purple/20 text-civix-purpleSoft font-medium">
                            {l.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
